import { useCallback, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

/**
 * useMonthlySnapshotExport — encapsulates the "render off-screen ⟶
 * PNG ⟶ share/download/copy" flow used by MonthlySnapshotModal.
 *
 * Returns:
 *   cardRef    — attach to the SnapshotCard's outer div
 *   exporting  — boolean flag while a PNG is being generated
 *   downloadPng(filenameBase)
 *   shareImage(filenameBase, shareText, shareTitle)
 *   copyOnly(shareText)
 *
 * Extracted from MonthlySnapshotModal.jsx (iter-45) to shrink that file
 * below 150 lines and to make the flow re-usable if we ever add more
 * shareable card variants (goal completion, streak milestone, etc.).
 */
export function useMonthlySnapshotExport() {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const { t } = useTranslation();

  const generatePng = useCallback(async () => {
    if (!cardRef.current) return null;
    setExporting(true);
    try {
      return await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#1A2E22",
        skipFonts: true,
      });
    } catch (e) {
      console.error("Monthly snapshot PNG failed:", e);
      toast.error(t("goal_complete_modal.toast_create_image_failed"));
      return null;
    } finally {
      setExporting(false);
    }
  }, [t]);

  const downloadPng = useCallback(async (filenameBase) => {
    const url = await generatePng();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filenameBase}.png`;
    a.click();
    toast.success(t("goal_complete_modal.toast_image_saved"));
  }, [generatePng, t]);

  const shareImage = useCallback(async (filenameBase, shareText, shareTitle) => {
    const url = await generatePng();
    if (!url) return;
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `${filenameBase}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: shareTitle, text: shareText, files: [file] });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.warn("File share unsupported — falling back", e);
    }
    // Fallback: download + copy caption
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filenameBase}.png`;
    a.click();
    try { await navigator.clipboard.writeText(shareText); } catch { /* ignore */ }
    toast.success(t("goal_complete_modal.toast_image_saved_caption"));
  }, [generatePng, t]);

  const copyOnly = useCallback(async (shareText) => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success(t("common.toast_copied"));
    } catch {
      toast.error(t("goal_complete_modal.toast_clipboard_blocked"));
    }
  }, [t]);

  return { cardRef, exporting, downloadPng, shareImage, copyOnly };
}
